import DeleteBlog from '@/components/pages/blog-dashboard/DeleteBlog';
import {
    Pagination,
    PaginationContent,
    PaginationItem,
    PaginationLink,
    PaginationNext
} from "@/components/ui/pagination";
import { FileSearch } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
interface BlogRightSideProps {
    displayBlogs: Blog[];
    searchTerm: string;
    isEditor?: boolean;
    page: number;
    setPage: (page: number) => void;
}

const BlogRightSide = ({ displayBlogs, searchTerm, isEditor, page, setPage }: BlogRightSideProps) => {
    return (
        <>
            <main className='flex flex-col gap-4 w-full sm:hidden mt-10'>
                {/* Results */}
                <div className='flex flex-col gap-8'>
                    {displayBlogs.length > 0 ? (
                        displayBlogs.map((blog, index) => (
                            <Link aria-label='blog cards' key={index} href={isEditor ? `/blog-dashboard/blogs/${blog.slug}` : `/blog-details/${blog.slug}`}>
                                <aside className='border p-2 rounded-lg flex gap-4'>
                                    <Image
                                        src={
                                            blog.image || '/assets/images/services/Construction.jpg'
                                        }
                                        alt={`Image of ${blog.image_alt}.` || "No image url"}
                                        height={400}
                                        width={400}
                                        fetchPriority='high'
                                        className='object-cover h-[6rem] w-[6rem] rounded-lg'
                                    />
                                    <div>
                                        <span className='text-xs text-muted-foreground'>{new Date(blog.created_at).toLocaleDateString()}</span>
                                        <h2 className='font-bold text-lg w-full text-wrap max-w-[18rem]'>{blog.title.slice(0, 52)}</h2>
                                    </div>
                                </aside>
                            </Link>
                        ))
                    ) : (
                        <p>No blogs found.</p>
                    )}
                </div>

                {/* Pagination with direct page selection */}
                {!searchTerm.trim() && (
                    <div className='flex gap-4 mt-10'>
                        <Pagination>
                            <PaginationContent>
                                {Array.from({ length: 5 }).map((_, index) => {
                                    const pageNumber = index + 1;
                                    return (
                                        <PaginationItem key={index}>
                                            <Link href={`?page=${pageNumber}`} passHref legacyBehavior>
                                                <PaginationLink onClick={() => setPage(pageNumber)}>
                                                    {pageNumber}
                                                </PaginationLink>
                                            </Link>
                                        </PaginationItem>
                                    );
                                })}
                                <PaginationItem>
                                    <Link href={`?page=${page + 1}`} passHref legacyBehavior>
                                        <PaginationNext onClick={() => setPage(page + 1)} />
                                    </Link>
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                )}
            </main>
            <main className='hidden sm:flex flex-col gap-10 w-full'>
                {/* Results */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {displayBlogs.length > 0 ? (
                        displayBlogs.map((blog, index) => (
                            <div
                                key={index}
                                className="rounded-2xl bg-white border border-gray-200 hover:border-gray-300 shadow-sm hover:shadow-md transition-all duration-300 group overflow-hidden"
                            >
                                <Link href={isEditor ? `/blog-dashboard/blogs/${blog.slug}` : `/blog-details/${blog.slug}`}>
                                    <div className="flex flex-col h-full">
                                        {/* Image */}
                                        <div className="p-4 flex justify-center items-center">
                                            <Image
                                                src={blog.image || '/assets/images/services/Construction.jpg'}
                                                alt={`Image of ${blog.image_alt}.` || 'Blog cover image'}
                                                width={500}
                                                height={500}
                                                fetchPriority='high'
                                                className="rounded-lg  border border-gray-100 shadow-sm"
                                            />
                                        </div>

                                        {/* Content */}
                                        <div className="px-4 pb-4 flex flex-col flex-grow">
                                            <h3 className="text-lg font-semibold text-gray-800 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                                                {blog.title}
                                            </h3>

                                            {isEditor && (
                                                <div className="mt-auto pt-3 border-t border-gray-100">
                                                    <div className="flex justify-between items-center">
                                                        <span
                                                            className={`inline-flex items-center gap-2 px-3 py-1 text-xs font-medium rounded-full ${blog.isPublished ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                                                                }`}
                                                        >
                                                            <span
                                                                className={`w-2 h-2 rounded-full ${blog.isPublished ? 'bg-green-500' : 'bg-yellow-500'
                                                                    }`}
                                                            ></span>
                                                            {blog.isPublished ? 'Published' : 'Draft'}
                                                        </span>

                                                        <DeleteBlog blogId={blog.id} />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            </div>

                        ))
                    ) : (
                        <div className="col-span-full py-12 text-center">
                            <div className="mx-auto max-w-md">
                                <FileSearch className="mx-auto h-12 w-12 text-gray-400" />
                                <h3 className="mt-4 text-lg font-semibold text-gray-800">No blogs found</h3>
                                <p className="mt-2 text-sm text-gray-500">Try adjusting your search or create a new blog</p>
                            </div>
                        </div>
                    )}
                </div>


                {/* Pagination with direct page selection */}
                {!searchTerm.trim() && (
                    <div className='flex gap-4 mt-10'>
                        <Pagination>
                            <PaginationContent>
                                {Array.from({ length: 5 }).map((_, index) => {
                                    const pageNumber = index + 1;
                                    const isActive = pageNumber === page;

                                    return (
                                        <PaginationItem key={index}>
                                            <Link href={`?page=${pageNumber}`} passHref legacyBehavior>
                                                <PaginationLink
                                                    onClick={() => setPage(pageNumber)}
                                                    className={`${isActive
                                                        ? "bg-blue-500 text-white hover:bg-blue-600"
                                                        : "hover:bg-gray-200"} py-2 px-4 rounded-xl`}
                                                    aria-current={isActive ? "page" : undefined}
                                                >
                                                    {pageNumber}
                                                </PaginationLink>
                                            </Link>
                                        </PaginationItem>
                                    );
                                })}

                                <PaginationItem>
                                    <Link href={`?page=${page + 1}`} passHref legacyBehavior>
                                        <PaginationNext
                                            className='hover:bg-gray-200 bg-gray-100'
                                            onClick={() => setPage(page + 1)}
                                        />
                                    </Link>
                                </PaginationItem>
                            </PaginationContent>
                        </Pagination>
                    </div>
                )}
            </main>
        </>
    )
}

export default BlogRightSide